import { Location } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

import {
  ControlValues,
  ParamMapping,
} from '../../interfaces/query-params.interface';
import { UiHelpersService } from '../ui-helpers/ui-helpers.service';

type FormParamValue = boolean | string | string[] | null | undefined;

@Injectable({
  providedIn: 'root',
})
export class QueryParamsService {
  readonly route = inject(ActivatedRoute);
  readonly location = inject(Location);
  readonly uiHelpers = inject(UiHelpersService);

  /**
   * Updates the form controls and session storage based on the provided query parameters and mappings.
   *
   * @param {FormGroup} form - The form group whose controls will be updated.
   * @param {any} queryParams - The query parameters to apply to the form.
   * @param {ParamMapping[]} [values] - Optional mappings that set the sessionStorage.
   *
   * If `values` is provided, the method updates form controls based on the
   * provided mappings and synchronizes session storage and query parameters.
   *
   */

  refreshParams(form: FormGroup, queryParams: any, values?: ParamMapping[]) {
    // Apply query parameters to the form if available
    if (queryParams) {
      const formattedParams = { ...queryParams };
      Object.keys(formattedParams).forEach(key => {
        const control = form.get(key);
        if (control) {
          formattedParams[key] = this.formatValue(
            control.value,
            formattedParams[key]
          );
        }
      });
      form.patchValue(formattedParams, {
        emitEvent: false,
      });
    }

    // Process each parameter mapping to update form controls
    if (!values) {
      return;
    }
    values.forEach(mapping => {
      const { queryParamKey, storageValue } = mapping;
      const queryParam = queryParams[queryParamKey];
      const individualControl = form.get(mapping.queryParamKey);
      if (!individualControl) {
        return;
      }
      this.updateControlValue(
        {
          queryParamValue: queryParam,
          storageValue,
          individualControl: individualControl as FormControl,
        },
        form
      );
    });
  }

  private updateControlValue(controlValues: ControlValues, form: FormGroup) {
    // Prioritize query parameter value over session storage value
    const storageKey = sessionStorage.getItem(controlValues.storageValue ?? '');

    const value = controlValues.queryParamValue ?? storageKey;

    if (value !== null && value !== undefined) {
      // Smart conversion
      const formattedValue = this.formatValue(
        controlValues.individualControl.value,
        value
      );

      // Set the value on the form control
      controlValues.individualControl.setValue(formattedValue);

      // If using value from storage (not from URL), update URL query parameters
      if (controlValues.queryParamValue == null && controlValues.storageValue) {
        this.updateQueries(controlValues, form);
      }
    }
  }

  private formatValue(
    currentValue: FormParamValue,
    incomingValue: FormParamValue
  ): FormParamValue {
    if (incomingValue === null || incomingValue === undefined) {
      return incomingValue;
    }

    // Smart conversion: Array
    if (
      Array.isArray(currentValue) &&
      !Array.isArray(incomingValue) &&
      typeof incomingValue === 'string'
    ) {
      return [incomingValue];
    }

    // Smart conversion: Boolean
    if (
      typeof currentValue === 'boolean' &&
      typeof incomingValue !== 'boolean'
    ) {
      return String(incomingValue).toLowerCase() === 'true';
    }

    return incomingValue;
  }

  private updateQueries(queriesValues: ControlValues, form: FormGroup) {
    // Convert form values to query parameter string
    const queryParams = this.uiHelpers.toQueryString(form.value);
    const newUrl = `${this.location.path().split('?')[0]}?${queryParams}`;
    // Save value to session storage
    if (queriesValues.storageValue) {
      sessionStorage.setItem(
        queriesValues.storageValue,
        queriesValues.individualControl.value
      );
    }
    // Update the URL without reloading the page
    this.location.replaceState(newUrl);
  }

  updateAllQueries(form: FormGroup, values?: ParamMapping[]) {
    const newUrl = `${this.location.path().split('?')[0]}?${this.uiHelpers.toQueryString(form.value)}`;
    values?.forEach(mapping => {
      if (mapping.storageValue) {
        const individualControl = form.get(mapping.queryParamKey);
        sessionStorage.setItem(
          mapping.storageValue,
          individualControl?.value || ''
        );
      }
    });

    this.location.replaceState(newUrl);
  }
}
